import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AuthLayout from './AuthLayout';
import { Mail, Lock, User, AlertCircle, ArrowRight } from 'lucide-react';

const RegisterPage = ({ onNavigate }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await register(email, password, name);
      if (result.require_approval) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout title="Account Created">
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(0,200,150,0.12)', border: '1px solid rgba(0,200,150,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <User size={24} color="#00C896" />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--c-text)', marginBottom: 8 }}>
            Registration Successful
          </h3>
          <p style={{ fontSize: 14, color: 'var(--c-text-2)', marginBottom: 8, lineHeight: 1.5 }}>
            Your account has been created and is pending administrator approval.
          </p>
          <p style={{ fontSize: 14, color: 'var(--c-text-2)', marginBottom: 24, lineHeight: 1.5 }}>
            Please contact the administrator to activate your account.
          </p>
          <button
            onClick={() => onNavigate('login')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--c-primary)', fontWeight: 600, fontSize: 14,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Return to Login
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle={
        <>
          Already have an account?{' '}
          <button
            onClick={() => onNavigate('login')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--c-primary)', fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Sign in
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.15)',
            borderRadius: 10, padding: '12px 16px',
            color: 'var(--c-red)', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div>
          <label style={{ color: 'var(--c-text-2)', fontSize: 12, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Full Name
          </label>
          <div style={{ position: 'relative' }}>
            <User size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
            <input
              type="text" required autoComplete="name"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              style={{ width: '100%', paddingLeft: 42, height: 48, borderRadius: 10 }}
            />
          </div>
        </div>

        <div>
          <label style={{ color: 'var(--c-text-2)', fontSize: 12, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Email Address
          </label>
          <div style={{ position: 'relative' }}>
            <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
            <input
              type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              style={{ width: '100%', paddingLeft: 42, height: 48, borderRadius: 10 }}
            />
          </div>
        </div>

        <div>
          <label style={{ color: 'var(--c-text-2)', fontSize: 12, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <Lock size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
            <input
              type="password" required autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={{ width: '100%', paddingLeft: 42, height: 48, borderRadius: 10 }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            marginTop: 6, height: 50, borderRadius: 12,
            background: 'linear-gradient(135deg, var(--c-primary), var(--c-primary-dim))',
            border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer',
            color: 'var(--c-btn-text)', fontWeight: 700, fontSize: 15,
            fontFamily: "'Inter', sans-serif",
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: isLoading ? 0.7 : 1,
            boxShadow: '0 8px 24px var(--c-primary-glow)',
            transition: 'all 0.2s',
          }}
        >
          {isLoading ? (
            <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
          ) : (
            <> Create Account <ArrowRight size={16} /> </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;
